<template>
  <div class='employees-issue-body'>
    <div class="description-block" :style="`flex-wrap: ${isShowAll ? 'wrap' : 'nowrap'};`">
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container" :class='{closed: !isShowAll}'>
        <div class='description'>{{description}}</div>
        <div class='description-title' v-if="explanation !== ''">{{$t('issueDataPopup.employees.explanation')}}</div>
        <div class='explanation' v-if="explanation !== ''">{{explanation}}</div>
        <div class='description-title' v-if='problems.length > 0'>{{$t('issueDataPopup.employees.problems')}}</div>
        <span class="problems-block" v-if='problems.length > 0'>
          <div v-for='(problem, index) in problems' :key='index'>
           <div class='problem-option'>
            <div class='data-title option'>
              <span class="material-icons bullet">
                lens_blur
              </span>
            </div>
             <div class='problem-text-container'>{{problem}}</div>
            </div>
          </div>
        </span>
      </div>
      <span class='hide-technologies-btn' @click.stop="isShowAll = !isShowAll">
          {{ captionOfTechnologiesToggleButton }}
      </span>
    </div>
    <div class="asset-block">
      <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
      <div class="text-container">
        {{tld}}
      </div>
    </div>
    <div class="tld-block">
      <div class="data-title">{{$t('issueDataPopup.employees.email')}}</div>
      <div class="text-container">
        {{asset}}
      </div>
    </div>
    <div class="breach-block">
      <div class='details-column' v-if='breachDate'>
        <div class="data-title" v-if='breachDate'>{{$t('issueDataPopup.employees.date')}}</div>
        <div class="text-container">
          {{breachDate}}
        </div>
      </div>
      <div class='details-column' v-if='leaks'>
        <div class="data-title" v-if='leaks'>{{$t('issueDataPopup.employees.leak.value')}}</div>
        <div v-for='(leak, index) in leaks' :key='index'>
          <v-text-field
            :type="showPassword[index] ? 'text' : 'password'"
            :append-icon="showPassword[index] ? 'visibility' : 'visibility_off'"
            @click:append="changePasswordInput(index)"
            :value="leak"
            class="leaked-data-input"
            readonly
          >
          </v-text-field>
        </div>
      </div>
    </div>
    <span class="solution-block" v-if='solutions.length > 0'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
        <div v-for='(solution, index) in solutions' :key='index'>
         <div class='mitigation-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
         <div class='text-container'>{{solution}}</div>
        </div>
      </div>
    </span>
    <div class="technical-block">
      <div class="technical-data">
        <div class='text-container' v-for='(sourceData, index) in sources' :key='index'>
          <div class='details-column technical-data'>
            <div class="data-sub-title">{{$t('issueDataPopup.employees.source.name')}}</div>
            <div class='text-container leak-by'>{{ sourceData.name }}</div>
          </div>
          <div class='details-column technical-data'>
            <div class="data-sub-title">{{$t('issueDataPopup.employees.source.description')}}</div>
            <div class='text-container leak-type' v-html='sourceData.description' />
          </div>
        </div>
      </div>
    </div>
    <div class="url-block" v-if="url !== ''">
      <div class="data-title">{{$t('issueDataPopup.employees.url')}}</div>
      <div class="text-container">
        <a :href='url' target="_blank"> {{url}} </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmployeesIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isShowAll: false,
      showPassword: [],
    };
  },
  mounted() {
    this.showPassword = new Array(this.leaks.length).fill(false);
  },
  methods: {
    changePasswordInput(index) {
      this.showPassword = [...this.showPassword];
      this.showPassword[index] = !this.showPassword[index];
    },
  },
  computed: {
    tld() {
      return this.issue.tld;
    },
    asset() {
      return this.issue.asset;
    },
    metaData() {
      return this.issue.metaData;
    },
    description() {
      return this.metaData.description;
    },
    credentials() {
      return this.metaData.leaks;
    },
    sources() {
      return this.metaData.sources;
    },
    breachDate() {
      return this.metaData.breachDate;
    },
    explanation() {
      return this.metaData?.explanation || '';
    },
    problems() {
      return this.metaData?.problems || [];
    },
    captionOfTechnologiesToggleButton() {
      return this.isShowAll ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
    solutions() {
      return this.metaData?.mitigation || [];
    },
    leaks() {
      return this.metaData.leaks[0] || [];
    },
    url() {
      return this.metaData?.url || '';
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.employees-issue-body {
  .details-column {
    display: inline-grid;
    width: fit-content;
    max-width: rem(650);
    margin-right: $space-4;
  }
  .description-block {
    display: block;
    margin-bottom: $space-5;
    width: rem(650);
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .description, .explanation {
      margin-bottom: $space-2;
    }
    .text-container {
      padding-right: $space-15;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      white-space: pre-line;
      &.closed {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        line-height: 1.2em;
        height: 3.6em;
      }
      .description-title {
        font-weight: bold;
        font-size: rem(13);
        margin-bottom: $space-2;
      }
      .problems-block {
        display: block;
        .bullet {
          flex: 1;
        }
        .problem-option {
          display: flex;
          .problem-text-container {
            margin-bottom: $space-3;
          }
        }
        .material-icons {
          font-size: rem(15);
          opacity: 0.5;
          margin-top: rem(3);
          margin-right: rem(5);
        }
      }
    }
  }
  .url-block {
    width: rem(650);
  }
  .solution-block {
    display: block;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-left: rem(5);
      padding-right: $space-15;
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .mitigation-option {
      display: flex;
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .technical-block {
    padding-bottom: $space-5;
    display: block;
  }
  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
    min-width: rem(115);
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: rem(10);
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .asset-block {
    margin-bottom: $space-5;
  }
  .tld-block {
    margin-bottom: $space-5;
  }
  .breach-block {
    margin-bottom: $space-2;
  }
  .hide-technologies-btn {
    color: var(--brand-primary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    letter-spacing: 0;
    &:hover {
      cursor: pointer;
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.employees-issue-body {
  .v-icon{
    font-size: rem(18);
    color: var(--text-secondary);
  }
  max-width: 70%;
  .v-text-field > .v-input__control > .v-input__slot:before {
    border-style: none;
  }
  .v-text-field > .v-input__control > .v-input__slot:after {
    border-style: none;
  }
  .v-text-field {
    margin-top: rem(-4);
    padding-top: 0;
  }
}
</style>
